/**
 * Website chat transcript = the SERVER history, nothing else.
 *
 * The widget used to keep a second copy of the conversation in localStorage
 * and reconcile it with server history by exact text match. Two sources of
 * truth meant every difference in persisted text (a page-context prefix, an
 * attachment placeholder, a rewrite) orphaned rows and hid replies. This
 * module derives everything the UI needs from the server rows plus a tiny
 * read watermark:
 *
 *   - greeting / nudge are UI-only pseudo-messages (no agent turn, no row)
 *   - read/unread comes from `lastReadMessageId` and two synthetic flags
 *   - in-flight optimistic bubbles live in component state, never persisted
 *
 * Dependency-free on purpose so node's test runner covers it directly.
 */

import type { IReadState, ITranscriptMessage } from '../interfaces/chatTranscript';

export type { IReadState, ITranscriptMessage };

export const EMPTY_READ_STATE: IReadState = {
  lastReadMessageId: 0,
  greetingRead: false,
  nudgeRead: false,
};

export function parseReadState(raw: string | null): IReadState {
  if (!raw) return EMPTY_READ_STATE;
  try {
    const parsed = JSON.parse(raw) as Partial<IReadState>;
    if (
      typeof parsed !== 'object' ||
      parsed === null ||
      typeof parsed.lastReadMessageId !== 'number' ||
      !Number.isFinite(parsed.lastReadMessageId)
    ) {
      return EMPTY_READ_STATE;
    }
    return {
      lastReadMessageId: Math.max(0, Math.floor(parsed.lastReadMessageId)),
      greetingRead: parsed.greetingRead === true,
      nudgeRead: parsed.nudgeRead === true,
    };
  } catch {
    return EMPTY_READ_STATE;
  }
}

export function serializeReadState(state: IReadState): string {
  return JSON.stringify(state);
}

function isIncoming(message: ITranscriptMessage): boolean {
  return (
    message.ai_chatbot_message_role !== 'user' &&
    message.ai_chatbot_message_role !== 'system'
  );
}

export function newestServerId(serverMessages: readonly ITranscriptMessage[]): number {
  return serverMessages.reduce(
    (max, m) => Math.max(max, m.ai_chatbot_message_id ?? 0),
    0
  );
}

/**
 * The full message list the panel renders, with read flags derived from the
 * watermark, plus the unread subset (drives the badge and the popup).
 */
export function buildTranscript(input: {
  serverMessages: readonly ITranscriptMessage[];
  greeting: ITranscriptMessage | null;
  nudge: ITranscriptMessage | null;
  readState: IReadState;
}): { messages: ITranscriptMessage[]; unread: ITranscriptMessage[] } {
  const { readState } = input;
  const flagged = (m: ITranscriptMessage, read: boolean): ITranscriptMessage => ({
    ...m,
    ai_chatbot_message_read: read ? 1 : 0,
  });
  const messages: ITranscriptMessage[] = [];
  if (input.greeting) messages.push(flagged(input.greeting, readState.greetingRead));
  for (const m of input.serverMessages) {
    const read =
      !isIncoming(m) || (m.ai_chatbot_message_id ?? 0) <= readState.lastReadMessageId;
    messages.push(flagged(m, read));
  }
  if (input.nudge) messages.push(flagged(input.nudge, readState.nudgeRead));
  return {
    messages,
    unread: messages.filter(m => isIncoming(m) && m.ai_chatbot_message_read === 0),
  };
}

/** Everything currently shown is now read. The watermark never regresses. */
export function markTranscriptRead(
  state: IReadState,
  serverMessages: readonly ITranscriptMessage[],
  shown: { greetingShown?: boolean; nudgeShown?: boolean }
): IReadState {
  return {
    lastReadMessageId: Math.max(state.lastReadMessageId, newestServerId(serverMessages)),
    greetingRead: state.greetingRead || shown.greetingShown === true,
    nudgeRead: state.nudgeRead || shown.nudgeShown === true,
  };
}

function timestampValue(timestamp: string): number {
  const value = Date.parse(
    timestamp.includes('T') ? timestamp : timestamp.replace(' ', 'T')
  );
  return Number.isNaN(value) ? Number.NaN : value;
}

/** The follow-up nudge appears once, after the assistant's latest reply has
 *  sat unanswered for `delayMs`. */
export function nudgeDue(input: {
  serverMessages: readonly ITranscriptMessage[];
  now: number;
  delayMs: number;
  alreadyShown: boolean;
}): boolean {
  if (input.alreadyShown) return false;
  const last = input.serverMessages[input.serverMessages.length - 1];
  if (!last || last.ai_chatbot_message_role !== 'assistant') return false;
  const at = timestampValue(last.ai_chatbot_message_insert_time);
  if (Number.isNaN(at)) return false;
  return input.now - at >= input.delayMs;
}

/**
 * Server timestamps are naive "YYYY-MM-DD HH:MM:SS" strings in UTC (the DB
 * runs in UTC). `Date.parse` treats that shape as LOCAL time, which showed
 * Taipei visitors their chat eight hours in the past. Tag them as UTC so every
 * consumer (bubble times, nudge timing) converts to the viewer's zone.
 */
export function serverTimestampToIso(timestamp: string | null | undefined): string | null {
  if (!timestamp) return null;
  const match = /^(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2}:\d{2})$/.exec(timestamp.trim());
  if (!match) return timestamp;
  return `${match[1]}T${match[2]}Z`;
}
